import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle2, AlertCircle, AlertTriangle, Copy, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { DjayNarrator } from '../../../mascot';
import { type Classroom, sanitizeDisplayName } from '../../../accounts';
interface BulkImportProps {
  teacherId: string;
  onComplete: () => void;
  onCancel: () => void;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
}
interface ParsedStudent {
  name: string;
  grade: number;
  status: 'pending' | 'success' | 'error';
  accountId?: string;
  linkCode?: string;
  errorMsg?: string;
  warnings: string[];
  skip?: boolean;
}
export function BulkImport({
  teacherId,
  onComplete,
  onCancel,
  classrooms = [],
  selectedClassroomId = null
}: BulkImportProps) {
  const {
    createAndLinkStudent,
    accounts
  } = useAppStore();
  const defaultClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const [inputText, setInputText] = useState('');
  const [defaultGrade, setDefaultGrade] = useState(defaultClassroom ? defaultClassroom.groupLevel.toString() : '6');
  const [targetClassroomId, setTargetClassroomId] = useState<string>(selectedClassroomId || '');
  const [parsedStudents, setParsedStudents] = useState<ParsedStudent[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'input' | 'preview' | 'result'>('input');
  const [copied, setCopied] = useState(false);
  /** Get all linked learners for duplicate detection */
  const getExistingLearners = (): Array<{
    name: string;
    grade: number;
  }> => {
    if (!accounts?.relationships?.teacherToLearners?.[teacherId]) return [];
    return accounts.relationships.teacherToLearners[teacherId].map(id => {
      const account = accounts.accountsById[id];
      if (account && account.role === 'learner') {
        return {
          name: account.displayName?.toLowerCase().trim() || '',
          grade: account.grade || 0
        };
      }
      return null;
    }).filter((l): l is {
      name: string;
      grade: number;
    } => l !== null);
  };
  const handleParse = () => {
    if (!inputText.trim()) return;
    const lines = inputText.split('\n').filter(line => line.trim().length > 0);
    const targetClassroom = classrooms.find(c => c.id === targetClassroomId);
    const existingLearners = getExistingLearners();
    const seenNames = new Set<string>();
    const students: ParsedStudent[] = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      const rawName = parts[0];
      const name = rawName ? sanitizeDisplayName(rawName.trim()) : '';
      let grade = parseInt(defaultGrade, 10);
      if (parts.length > 1) {
        const parsedGrade = parseInt(parts[1], 10);
        if (!isNaN(parsedGrade) && parsedGrade >= 1 && parsedGrade <= 8) {
          grade = parsedGrade;
        }
      }
      const warnings: string[] = [];
      let hasError = false;
      // Validate: empty name
      if (!name || name.trim().length === 0) {
        warnings.push('Naam is leeg');
        hasError = true;
      }
      // Validate: name too short
      if (name && name.length > 0 && name.length < 2) {
        warnings.push('Naam moet minimaal 2 tekens zijn');
        hasError = true;
      }
      // Validate: name too long
      if (name.length > 100) {
        warnings.push('Naam mag maximaal 100 tekens zijn');
        hasError = true;
      }
      // Validate: grade mismatch with target classroom
      if (targetClassroom && grade !== targetClassroom.groupLevel) {
        warnings.push(`Groep ${grade} komt niet overeen met klas "${targetClassroom.name}" (groep ${targetClassroom.groupLevel})`);
      }
      // Validate: duplicate with existing learners
      const normalizedName = name?.toLowerCase().trim() || '';
      if (normalizedName) {
        const isDuplicate = existingLearners.some(l => l.name === normalizedName && l.grade === grade);
        if (isDuplicate) {
          warnings.push(`"${name}" (groep ${grade}) is al gekoppeld aan je account`);
        }
      }
      // Validate: duplicate within import list
      if (normalizedName && seenNames.has(normalizedName)) {
        warnings.push(`"${name}" komt meerdere keren voor in de lijst`);
      }
      seenNames.add(normalizedName);
      return {
        name,
        grade,
        status: hasError ? 'error' : 'pending',
        warnings,
        skip: false
      };
    });
    setParsedStudents(students);
    setStep('preview');
  };
  const handleImport = async () => {
    setIsProcessing(true);
    const results = [...parsedStudents];
    for (let i = 0; i < results.length; i++) {
      const student = results[i];
      if (student.status === 'error' || student.skip) continue; // Skip invalid or opted-out entries
      try {
        // F4: Atomic operation — create account, link to teacher, generate code in one state update
        const {
          studentId,
          linkCode
        } = createAndLinkStudent(teacherId, {
          displayName: student.name,
          grade: student.grade
        }, targetClassroomId || undefined);
        results[i] = {
          ...student,
          status: 'success',
          accountId: studentId,
          linkCode
        };
      } catch (err) {
        results[i] = {
          ...student,
          status: 'error',
          errorMsg: 'Fout bij aanmaken'
        };
      }
    }
    setParsedStudents(results);
    setIsProcessing(false);
    setStep('result');
  };
  const copyAllCodes = () => {
    const text = parsedStudents.filter(s => s.status === 'success' && s.linkCode).map(s => `${s.name}: ${s.linkCode}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const totalWarnings = parsedStudents.reduce((sum, s) => sum + s.warnings.length, 0);
  return <div className="space-y-4" data-id="element-8732">
      {step === 'input' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-8733">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-slate-600" data-id="element-8734">
            <p className="font-semibold text-slate-800 mb-1" data-id="element-8735">Hoe werkt het?</p>
            <p data-id="element-8736">
              Plak een lijst met namen (één per regel). Je kunt optioneel de
              groep toevoegen met een komma (bijv. "Emma de Vries, 7").
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8737">
            <Select variant="admin" label="Standaard groep (als niet opgegeven)" value={defaultGrade} onChange={e => setDefaultGrade(e.target.value)} options={[{
          value: '6',
          label: 'Groep 6'
        }, {
          value: '7',
          label: 'Groep 7'
        }, {
          value: '8',
          label: 'Groep 8'
        }]} data-id="element-8738" />

            {classrooms.length > 0 && <Select variant="admin" label="Toevoegen aan klas" value={targetClassroomId} onChange={e => {
          const newId = e.target.value;
          setTargetClassroomId(newId);
          if (newId) {
            const cls = classrooms.find(c => c.id === newId);
            if (cls) setDefaultGrade(cls.groupLevel.toString());
          }
        }} placeholder="Geen klas" options={classrooms.map(c => ({
          value: c.id,
          label: c.name
        }))} data-id="element-8739" />}
          </div>

          <div data-id="element-8740">
            <label className="block text-sm font-medium text-slate-700 mb-1 flex justify-between" data-id="element-8741">
              <span data-id="element-8742">Namenlijst</span>
              <span className="text-slate-400 font-normal" data-id="element-8743">
                Eén leerling per regel
              </span>
            </label>
            <textarea value={inputText} onChange={e => setInputText(e.target.value)} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm" placeholder={'Emma de Vries\nNoah Jansen, 7\nSophie Bakker'} data-id="element-8744" />
          </div>

          <div className="pt-2 flex justify-end gap-2" data-id="element-8745">
            <Button variant="secondary" onClick={onCancel} data-id="element-8746">
              Annuleren
            </Button>
            <Button onClick={handleParse} disabled={!inputText.trim()} data-id="element-8747">
              <FileText className="w-4 h-4 mr-2" data-id="element-8748" />
              Namen controleren
            </Button>
          </div>
        </motion.div>}

      {step === 'preview' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-8749">
          <div className="flex items-center justify-between mb-2" data-id="element-8750">
            <h4 className="font-bold text-slate-800" data-id="element-8751">
              Controleer de lijst ({parsedStudents.length} leerlingen)
            </h4>
            <button onClick={() => setStep('input')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-8752">
              Lijst aanpassen
            </button>
          </div>

          {/* Validation warnings banner */}
          {totalWarnings > 0 && <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 flex items-start gap-3" data-id="element-8753">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8754" />
              <div data-id="element-8755">
                <h4 className="font-bold text-amber-900 text-sm" data-id="element-8756">
                  {totalWarnings} waarschuwing{totalWarnings !== 1 ? 'en' : ''}{' '}
                  gevonden
                </h4>
                <p className="text-xs text-amber-700 mt-0.5" data-id="element-8757">
                  Je kunt toch importeren, maar controleer de gemarkeerde rijen.
                </p>
              </div>
            </div>}

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-8758">
            <table className="w-full text-sm text-left" data-id="element-8759">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-8760">
                <tr data-id="element-8761">
                  <th className="px-4 py-2 font-medium w-10" data-id="element-8762">
                    <input type="checkbox" checked={parsedStudents.every(s => !s.skip)} onChange={e => {
                  const checked = e.target.checked;
                  setParsedStudents(parsedStudents.map(s => ({
                    ...s,
                    skip: !checked
                  })));
                }} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600" data-id="element-8763" />
                  </th>
                  <th className="px-4 py-2 font-medium" data-id="element-8764">Naam</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-8765">Groep</th>
                  <th className="px-4 py-2 font-medium w-10" data-id="element-8766"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-8767">
                {parsedStudents.map((student, idx) => <tr key={idx} className={`hover:bg-slate-50 ${student.skip ? 'opacity-50' : ''} ${student.status === 'error' ? 'bg-red-50/50' : student.warnings.length > 0 ? 'bg-amber-50/50' : ''}`} data-id="element-8768">
                    <td className="px-4 py-2" data-id="element-8769">
                      <input type="checkbox" checked={!student.skip} onChange={e => {
                  const newStudents = [...parsedStudents];
                  newStudents[idx] = {
                    ...student,
                    skip: !e.target.checked
                  };
                  setParsedStudents(newStudents);
                }} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600" data-id="element-8770" />
                    </td>
                    <td className={`px-4 py-2 font-medium ${student.status === 'error' ? 'text-red-800' : 'text-slate-800'}`} data-id="element-8771">
                      {student.name || '(Leeg)'}
                    </td>
                    <td className={`px-4 py-2 ${student.warnings.some(w => w.includes('Groep')) ? 'text-amber-700 font-bold' : 'text-slate-600'}`} data-id="element-8772">
                      Groep {student.grade}
                    </td>
                    <td className="px-4 py-2" data-id="element-8773">
                      {student.warnings.length > 0 && <span title={student.warnings.join('\n')} className="cursor-help" data-id="element-8774">
                          {student.status === 'error' ? <AlertCircle className="w-4 h-4 text-red-500" data-id="element-8775" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-8776" />}
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Per-student warning details */}
          {totalWarnings > 0 && <div className="space-y-1" data-id="element-8777">
              {parsedStudents.filter(s => s.warnings.length > 0).map((s, idx) => <div key={idx} className={`text-xs px-3 py-1.5 rounded-lg border ${s.status === 'error' ? 'text-red-700 bg-red-50 border-red-100' : 'text-amber-700 bg-amber-50 border-amber-100'}`} data-id="element-8778">
                    <span className="font-bold" data-id="element-8779">{s.name || '(leeg)'}:</span>{' '}
                    {s.warnings.join('; ')}
                  </div>)}
            </div>}

          <div className="pt-2 flex justify-end gap-2" data-id="element-8780">
            <Button variant="secondary" onClick={onCancel} disabled={isProcessing} data-id="element-8781">
              Annuleren
            </Button>
            <Button onClick={handleImport} disabled={isProcessing || parsedStudents.filter(s => s.status !== 'error' && !s.skip).length === 0} data-id="element-8782">
              {isProcessing ? <>Bezig met importeren...</> : <>
                  <Upload className="w-4 h-4 mr-2" data-id="element-8783" />
                  Importeer{' '}
                  {parsedStudents.filter(s => s.status !== 'error' && !s.skip).length}{' '}
                  leerlingen
                </>}
            </Button>
          </div>
        </motion.div>}

      {step === 'result' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-8784">
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 flex items-start gap-3" data-id="element-8785">
            <div className="flex-shrink-0 mt-0.5" data-id="element-8786">
              <DjayNarrator size="sm" emotion="celebrating" data-id="element-8787" />
            </div>
            <div data-id="element-8788">
              <h4 className="font-bold text-emerald-900" data-id="element-8789">Import voltooid!</h4>
              <p className="text-sm text-emerald-700" data-id="element-8790">
                Er zijn{' '}
                {parsedStudents.filter(s => s.status === 'success').length}{' '}
                leerlingen toegevoegd
                {targetClassroomId ? ' aan je klas' : ' aan je account'}. Deel
                onderstaande koppelcodes met de leerlingen.
              </p>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-8791">
            <table className="w-full text-sm text-left" data-id="element-8792">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-8793">
                <tr data-id="element-8794">
                  <th className="px-4 py-2 font-medium" data-id="element-8795">Naam</th>
                  <th className="px-4 py-2 font-medium w-32" data-id="element-8796">Koppelcode</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-8797">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-8798">
                {parsedStudents.map((student, idx) => <tr key={idx} className="hover:bg-slate-50" data-id="element-8799">
                    <td className="px-4 py-2 font-medium text-slate-800" data-id="element-8800">
                      {student.name}
                    </td>
                    <td className="px-4 py-2" data-id="element-8801">
                      {student.linkCode ? <span className="font-mono font-bold text-indigo-700 tracking-wider bg-indigo-50 px-2 py-1 rounded" data-id="element-8802">
                          {student.linkCode}
                        </span> : '-'}
                    </td>
                    <td className="px-4 py-2" data-id="element-8803">
                      {student.status === 'success' ? <span className="text-emerald-600 flex items-center gap-1 text-xs font-medium" data-id="element-8804">
                          <CheckCircle2 className="w-3 h-3" data-id="element-8805" /> Gelukt
                        </span> : <span className="text-red-600 flex items-center gap-1 text-xs font-medium" title={student.errorMsg} data-id="element-8806">
                          <AlertCircle className="w-3 h-3" data-id="element-8807" /> Fout
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="pt-2 flex justify-between items-center" data-id="element-8808">
            <Button variant="secondary" onClick={copyAllCodes} className="bg-white" data-id="element-8809">
              {copied ? <>
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" data-id="element-8810" />{' '}
                  Gekopieerd!
                </> : <>
                  <Copy className="w-4 h-4 mr-2" data-id="element-8811" /> Kopieer alle codes
                </>}
            </Button>
            <Button onClick={onComplete} data-id="element-8812">Klaar</Button>
          </div>
        </motion.div>}
    </div>;
}